import { SYLA_EARLIEST_EVENT_DATE } from "@syla/shared/types/helpers/constants";
import { addYears } from "date-fns";
import React from "react";
import { AccountTimeZone } from "../../../../../constants/AccountTimeZone";
import { useGetAccountFinancialYearsData } from "../../../../../store/actions/userFinancialYear";
import { OrderFilterSetting } from "../../../../../types/order/orderQuery";
import {
  DateRangePicker,
  DateRangePickerProps,
} from "../../../../atoms/datePicker/DateRangePicker";
import {
  startOfDayInTimeZone,
  addDaysInTimezone,
  startOfMinuteInTimeZone,
} from "../../../../atoms/datePicker/tzDateUtils";

type DateFilterProps = Omit<
  DateRangePickerProps,
  | "timeZone"
  | "earliestDate"
  | "latestDate"
  | "dateRange"
  | "presetTitle"
  | "presetDateRanges"
  | "onChange"
> & {
  value: OrderFilterSetting["dateRange"];
  onChange: (value: OrderFilterSetting["dateRange"]) => void;
};

export function DateFilter({
  value,
  onChange,
  ...props
}: {
  value: OrderFilterSetting["dateRange"];
  onChange: (value: OrderFilterSetting["dateRange"]) => void;
} & DateFilterProps): React.ReactElement {
  const financialYears = useGetAccountFinancialYearsData();

  const financialYearPresetRanges = React.useMemo(
    () =>
      (financialYears.data ?? []).map((fy) => ({
        key: fy.value,
        label: `FY ${fy.endDate.getFullYear()}`,
        dateRange: {
          from: fy.startDate,
          // The date range picker is day-only, so we also select the start of the
          // day to be consistent with the internal working of the day picker.
          // This will get converted to an end of day date for the filter options.
          to: startOfDayInTimeZone(fy.endDate, AccountTimeZone),
        },
        financialYearValue: fy.value,
      })),
    [financialYears.data],
  );

  return (
    <DateRangePicker
      timeZone={AccountTimeZone}
      earliestDate={SYLA_EARLIEST_EVENT_DATE}
      latestDate={startOfMinuteInTimeZone(
        addYears(new Date(), 50),
        AccountTimeZone,
      )}
      dateRange={
        value && value[0] !== null && value[1] !== null ?
          {
            from: value[0],
            // convert the start of the next day, back into the actual
            // selected day for the date picker
            to: addDaysInTimezone(value[1], -1, AccountTimeZone),
          }
        : undefined
      }
      presetTitle="Financial years"
      presetDateRanges={financialYearPresetRanges}
      onChange={(dateRange) => {
        // The date selector works in day-only selection returning the start of
        // each day selected. For the date filter, we want all transactions
        // up to the end of the selected day. Since the API needs an exclusive end
        // date, we add 1 day to get the very start of the next day.
        const newDateRange: OrderFilterSetting["dateRange"] =
          dateRange ?
            [
              dateRange.from || null,
              dateRange.to ?
                addDaysInTimezone(dateRange.to, 1, AccountTimeZone)
              : null,
            ]
          : undefined;

        onChange(newDateRange);
      }}
      {...props}
    />
  );
}
